import { FolderPlusIcon, UsersRoundIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { normalizeQuery } from '@/shared/lib/strings';
import type { CommandItem, CommandSource } from '@/shared/registry/types';

import { useGroupList } from './api';
import { GroupAvatar } from './components/GroupBits';
import { groupPath } from './lib/groups';

const matches = (query: string, ...texts: string[]) => {
  const q = query.trim().toLocaleLowerCase();
  return !q || texts.some((text) => text.toLocaleLowerCase().includes(q));
};

/** "Create a group / project" in the ⌘K palette. */
function useCreateItems(query: string): { items: CommandItem[] } {
  const { t } = useTranslation('groups');
  const items: CommandItem[] = [
    {
      id: 'groups:create',
      label: t('palette.createGroup'),
      icon: UsersRoundIcon,
      keywords: [t('palette.keywords')],
      to: '/groups?tab=all&create=1',
    },
    {
      id: 'groups:create-project',
      label: t('palette.createProject'),
      icon: FolderPlusIcon,
      keywords: [t('palette.keywords')],
      to: '/groups?tab=projects&create=project',
    },
  ];
  return {
    items: query.trim() ? items.filter((item) => matches(query, item.label, ...(item.keywords ?? []))) : [],
  };
}

/** Groups and projects matching the query (the user's first). */
function useGroupItems(query: string): { items: CommandItem[]; isLoading: boolean } {
  const { t } = useTranslation('groups');
  const search = normalizeQuery(query);
  const result = useGroupList({ search, pageSize: 6 }, { enabled: !!search });
  const items = (result.data?.items ?? []).map<CommandItem>((group) => ({
    id: `group:${group.id}`,
    label: group.name,
    description: [
      group.isProject ? t('type.project') : t('type.group'),
      t('card.members', { count: group.memberCount }),
      group.myRole ? t(`roles.${group.myRole}`) : null,
    ]
      .filter(Boolean)
      .join(' · '),
    leading: <GroupAvatar group={group} size="sm" />,
    to: groupPath(group.id),
  }));
  return { items, isLoading: result.isFetching };
}

export const groupCommandSources: CommandSource[] = [
  {
    id: 'groups',
    headingKey: 'groups:palette.heading',
    order: 60,
    minQueryLength: 2,
    useItems: useGroupItems,
  },
  { id: 'groups-actions', headingKey: 'groups:palette.actions', order: 61, useItems: useCreateItems },
];
